import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Truck, Wallet, Leaf, Clock, Package2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { eur } from '@/lib/format';
import { PublicHeader, Footer } from '@/components/Header';
import { useScheduleStatus } from '@/lib/schedule';
import Seo from '@/components/Seo';

export default function LandingPage() {
  const [featured, setFeatured] = useState([]);
  const [combos, setCombos] = useState([]);
  const schedule = useScheduleStatus();
  const nav = useNavigate();

  useEffect(() => {
    api.get('/signature-bowls').then(r => setFeatured(r.data.filter(b => b.featured).slice(0, 3)));
    api.get('/combos').then(r => setCombos((r.data || []).filter(c => c.featured !== false).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Poke Bowl a Catania | Delivery & Ritiro in negozio"
        description="Ordina online le migliori poke bowl a Catania: componi la tua bowl, scegli tra ricette firmate, consegna a domicilio o ritiro in negozio con pre-ordini e codici sconto."
        path="/"
        keywords="poke bowl catania, poke delivery, ordina poke online, MōMA poke, cucina hawaiana catania"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'MōMA • Pokè — Poke Bowl a Catania',
          description: 'Delivery e ritiro di poke bowl fresche a Catania.',
          url: 'https://www.momapoke.com/',
          inLanguage: 'it-IT',
          isPartOf: { '@id': 'https://www.momapoke.com/#website' },
          about: { '@id': 'https://www.momapoke.com/#restaurant' },
        }}
      />
      <PublicHeader />
      <main className="flex-1">
        {/* HERO: full-width blue panel with hero text (no medallion/stripes) */}
        <section className="relative overflow-hidden bg-[--brand-primary] flex items-center min-h-[70vh] md:min-h-[65vh] lg:min-h-[60vh]">
          <div className="max-w-6xl w-full mx-auto px-4 sm:px-8 lg:px-14 py-16 sm:py-20 lg:py-24">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="relative text-white max-w-2xl"
            >
              <Badge className="bg-white text-[--brand-primary] border-transparent rounded-full px-3 py-1 font-semibold">
                <Sparkles size={12} className="mr-1" /> Fresco ogni giorno
              </Badge>
              {schedule && (
                <div className="mt-3">
                  {schedule.is_open
                    ? <Badge className="bg-[#0F2C5C] text-white border border-white/25 rounded-full px-3 py-1">Aperto ora</Badge>
                    : <Badge className="bg-[#0F2C5C] text-white border border-white/25 rounded-full px-3 py-1"><Clock size={12} className="mr-1 inline"/>Riapre {schedule.next_open_display}</Badge>}
                </div>
              )}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 leading-[1.05] tracking-tight">
                Poke bowl<br/>
                <span className="italic font-normal">artigianali</span>,<br/>
                consegnate a casa.
              </h1>
              <p className="text-white/90 mt-4 max-w-lg text-base sm:text-lg">
                Ingredienti freschi e ricette autentiche del vero poke hawaiano. Componi la tua bowl o scegli tra le nostre firme, consegna rapida a Catania.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/builder" data-testid="landing-build-cta">
                  <Button size="lg" className="rounded-xl bg-white hover:bg-white/90 text-[--brand-primary] font-semibold shadow-[var(--shadow-md)]">
                    Crea la tua Poke <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/menu" data-testid="landing-menu-cta">
                  <Button size="lg" variant="secondary" className="rounded-xl bg-transparent text-white border border-white/40 hover:bg-white/10">
                    Vedi il menu
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mt-8 text-sm">
                <span className="flex items-center gap-1.5 bg-white text-[--brand-text] rounded-full px-3 py-1.5"><Leaf size={14}/> Ingredienti freschi</span>
                <span className="flex items-center gap-1.5 bg-white text-[--brand-text] rounded-full px-3 py-1.5"><Truck size={14}/> Consegna rapida</span>
                <span className="flex items-center gap-1.5 bg-white text-[--brand-text] rounded-full px-3 py-1.5"><Wallet size={14}/> Alla consegna</span>
              </div>
            </motion.div>
          </div>
        </section>

        {combos.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[--brand-accent] flex items-center gap-1"><Package2 size={12}/> Offerte speciali</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-[--brand-text]">Menù combo</h2>
              </div>
              <Link to="/menu" className="text-sm text-[--brand-primary] font-semibold hover:underline">
                Vedi tutte →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {combos.map((c, i) => (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  onClick={() => nav(`/menu#combo-${c.id}`)}
                  className="text-left rounded-2xl bg-white border-2 border-[--brand-accent]/30 overflow-hidden shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] hover:border-[--brand-accent] transition-shadow"
                  data-testid="landing-featured-combo"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[--brand-surface-2] relative">
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Package2 size={40} className="text-[--brand-muted]"/></div>
                    )}
                    <Badge className="absolute top-2 left-2 bg-[--brand-accent] text-white border-transparent">Combo</Badge>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-lg font-bold">{c.name}</h3>
                      <div className="tabular-nums font-bold text-[--brand-primary]">{eur(c.price)}</div>
                    </div>
                    {c.description && <p className="text-sm text-[--brand-muted] mt-1 line-clamp-2">{c.description}</p>}
                    <div className="text-xs text-[--brand-muted] mt-2">🥤 {c.drink_ids?.length || 0} bevande a scelta</div>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {featured.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Le più amate</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-[--brand-text]">Signature bowls</h2>
              </div>
              <Link to="/menu" className="text-sm text-[--brand-primary] font-semibold hover:underline">
                Vedi tutte →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((b, i) => (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow"
                  data-testid="landing-featured-bowl"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-lg font-bold">{b.name}</h3>
                      <div className="tabular-nums font-semibold text-[--brand-primary]">{eur(b.price)}</div>
                    </div>
                    <p className="text-sm text-[--brand-muted] mt-1 line-clamp-2">{b.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid gap-8 sm:grid-cols-3 text-center">
            {[
              { icon: Sparkles, title: 'Personalizza', text: 'Scegli base, proteine, topping e salse come vuoi tu.' },
              { icon: Truck, title: 'Consegna veloce', text: 'Rider dedicati, tracciamento live del tuo ordine.' },
              { icon: Wallet, title: 'Paghi alla consegna', text: 'Contanti o carta comodamente a casa tua.' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-2xl bg-[--brand-primary] text-white flex items-center justify-center shadow-[var(--shadow-sm)]">
                  <s.icon size={24} />
                </div>
                <div className="font-display font-bold text-lg mt-1">{s.title}</div>
                <p className="text-sm text-[--brand-muted] max-w-xs">{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

